import { useEffect, useState } from "react";
import { DAY_NAMES } from "./orderConstants";

/**
 * Dokedy sa da objednat na dnes -- odpocet do uzavierky.
 *
 * ZDROJ SU `dni` Z `/api/order-config`, nie rozvrh.
 *   Server v nich uz vyhodnotil vsetko, co rozhoduje o objednani (vypnute,
 *   pauza, zatvoreny den, chybajuce menu, R13). Keby sa tu rozvrh vyhodnocoval
 *   znova ako v dennom menu, uvod a kosik by vedeli ukazat "este 2 h" na den,
 *   ktory server odmietne.
 *
 * PREBEHNUTIE UZAVIERKY POCAS OTVORENEJ STRANKY:
 *   `objednatelny` je stav v case nacitania. Odpocet preto bezi ďalej sam
 *   a ked prejde uzavierka, vrati `po-uzavierke` -- kosik podla toho zavrie
 *   dnesok bez opatovneho nacitania. Server by objednavku aj tak odmietol,
 *   ale clovek sa to ma dozvediet skor, nez klikne.
 *
 * MINUTA UZAVIERKY ESTE PLATI:
 *   Server porovnava `minutyTeraz > koniec`, takze pri uzavierke 18:00 prejde
 *   objednavka este o 18:00:59. Tu je to rovnako: vyprsane je az od 18:01.
 *
 * Cas sa berie z hodin zariadenia, prepocitany do Europe/Bratislava. Zle
 * nastavene hodiny posunu len odpocet, nie pravidlo -- to drzi server.
 */

const ZONA = "Europe/Bratislava";

/** Pod tolko minut je odpocet nalehavy (ina farba). */
export const NALEHAVE_MINUT = 30;

/** Ako casto sa odpocet prepocita. Minuty staci obnovit raz za par sekund. */
const OBNOVA_MS = 15000;

/** { datum: 'YYYY-MM-DD', sekundy: od polnoci } v Bratislave. */
export function terazVBratislave(teraz = new Date()) {
  const casti = new Intl.DateTimeFormat("en-CA", {
    timeZone: ZONA,
    year: "numeric", month: "2-digit", day: "2-digit",
    hour: "2-digit", minute: "2-digit", second: "2-digit", hourCycle: "h23",
  }).formatToParts(teraz);
  const h = (typ) => casti.find((c) => c.type === typ).value;
  return {
    datum: `${h("year")}-${h("month")}-${h("day")}`,
    sekundy: Number(h("hour")) * 3600 + Number(h("minute")) * 60 + Number(h("second")),
  };
}

/** '18:00' alebo '18:00:00' -> 1080. */
function naMinuty(cas) {
  if (!cas) return null;
  const [h, m] = String(cas).split(":").map(Number);
  return Number.isFinite(h) ? h * 60 + (m || 0) : null;
}

/** 135 -> "2 h 15 min", 120 -> "2 h", 45 -> "45 min", 0 -> "menej ako minútu". */
export function formatZostava(minut) {
  if (minut < 1) return "menej ako minútu";
  const h = Math.floor(minut / 60);
  const m = minut % 60;
  if (h === 0) return `${m} min`;
  return m === 0 ? `${h} h` : `${h} h ${m} min`;
}

/** Den nasledujuci po 'YYYY-MM-DD' -- v UTC, aby letny cas nic neposunul. */
export function zajtra(datum) {
  const [r, m, d] = datum.split("-").map(Number);
  return new Date(Date.UTC(r, m - 1, d + 1)).toISOString().slice(0, 10);
}

/** '2026-10-10' (dnes je 9.) -> "zajtra (piatok 10. 10.)", inak "pondelok 13. 10." */
export function popisNajblizsieho(datum, dnes) {
  const [r, m, d] = datum.split("-").map(Number);
  const den = new Date(Date.UTC(r, m - 1, d)).getUTCDay();
  const nazov = DAY_NAMES[den === 0 ? 6 : den - 1].toLowerCase();
  const text = `${nazov} ${d}. ${m}.`;
  return datum === zajtra(dnes) ? `zajtra (${text})` : text;
}

/**
 * Stav dnesnej uzavierky. Cista funkcia -- `useUzavierka` ju len prepocitava.
 *
 * Vrati:
 *   { typ: "otvorene", dnes, do, zostavaMinut, nalehave }
 *   { typ: "po-uzavierke", dnes, do, najblizsi }   najblizsi = datum alebo null
 *   { typ: "dnes-nie", dnes, najblizsi }           dnes sa neda z ineho dovodu
 *   null                                           niet co povedat
 */
export function stavUzavierky(dni, teraz = new Date()) {
  if (!Array.isArray(dni) || dni.length === 0) return null;
  const { datum: dnes, sekundy } = terazVBratislave(teraz);
  const dnesny = dni.find((d) => d.datum === dnes) || null;
  const koniec = naMinuty(dnesny?.do);

  // Uzavierka prejde az po jej minute (pozri hore), preto `+ 60`.
  const poUzavierke = koniec !== null && sekundy >= koniec * 60 + 60;
  const najblizsi = dni.find((d) => d.objednatelny && d.datum > dnes)?.datum || null;

  if (dnesny?.objednatelny && koniec !== null && !poUzavierke) {
    const zostavaMinut = Math.max(0, Math.floor((koniec * 60 - sekundy) / 60));
    return {
      typ: "otvorene", dnes, do: dnesny.do, zostavaMinut, nalehave: zostavaMinut < NALEHAVE_MINUT,
    };
  }
  // Pri pauze sem volajuci posiela prazdne `dni` -- veta "je po uzavierke"
  // by vtedy zakryla skutocny dovod, ktory obrazovka ukazuje inde.
  if (dnesny && poUzavierke && !dnesny.zatvorene) {
    return { typ: "po-uzavierke", dnes, do: dnesny.do, najblizsi };
  }
  if (najblizsi) return { typ: "dnes-nie", dnes, najblizsi };
  return null;
}

/**
 * Stav uzavierky, ktory sa sam obnovuje. Pri navrate do karty (mobil uspava
 * casovace na pozadi) sa prepocita hned, nie az o 15 s.
 */
export function useUzavierka(dni) {
  const [teraz, setTeraz] = useState(() => new Date());

  useEffect(() => {
    const obnov = () => setTeraz(new Date());
    const casovac = setInterval(obnov, OBNOVA_MS);
    const priNavrate = () => { if (document.visibilityState === "visible") obnov(); };
    document.addEventListener("visibilitychange", priNavrate);
    return () => {
      clearInterval(casovac);
      document.removeEventListener("visibilitychange", priNavrate);
    };
  }, []);

  return stavUzavierky(dni, teraz);
}
